import { cn } from "@newxsystems/shared";
import type { ActionStyle, BlockAlignment } from "@newxsystems/types";
import type { AnchorHTMLAttributes } from "react";
import { getComponent } from "../../../../client";
import type { IBlockActionsProps } from "./BlockActions.props";

const actionClasses: Record<ActionStyle, string> = {
  primary:
    "border-foreground bg-foreground text-background hover:border-electric-blue hover:bg-electric-blue hover:text-white",
  secondary:
    "border-foreground bg-transparent text-foreground hover:bg-foreground hover:text-background",
  ghost: "border-transparent text-foreground hover:border-foreground",
  destructive: "border-destructive bg-destructive text-white hover:bg-destructive-strong",
};

const alignmentClasses: Record<BlockAlignment, string> = {
  start: "justify-start",
  center: "justify-center",
};

export const BlockActions = ({ actions, alignment = "start", className }: IBlockActionsProps) => {
  const Link = getComponent<AnchorHTMLAttributes<HTMLAnchorElement>>("Link");

  if (!Link || !actions.length) {
    return null;
  }

  return (
    <div className={cn("flex flex-wrap gap-3", alignmentClasses[alignment ?? "start"], className)}>
      {actions.map((action, index) => {
        const opensNewTab = action.openInNewTab === true;

        return (
          <Link
            aria-label={action.accessibilityLabel ?? action.label}
            className={cn(
              "inline-flex min-h-12 min-w-0 items-center justify-center rounded-full border px-5 text-center font-mono text-[12px]/[1.45] transition-colors duration-200 [overflow-wrap:anywhere] focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-ring",
              actionClasses[action.style ?? "primary"],
            )}
            href={action.href}
            key={`${action.href}-${index}`}
            rel={opensNewTab ? "noopener noreferrer" : undefined}
            target={opensNewTab ? "_blank" : undefined}
          >
            {action.label}
          </Link>
        );
      })}
    </div>
  );
};

BlockActions.displayName = "Shared::BlockActions";
